'use client';
import * as React from 'react';
import PropTypes from 'prop-types';
import { styled } from '@mui/material/styles';
import { StandaloneEvent as UnstyledStandaloneEvent } from '@mui/x-scheduler-internals/standalone-event';
import type { StandaloneEventProps } from './StandaloneEvent.types';
import { EventDragPreview } from '../internals/components/event-drag-preview';
import { EventCalendarStyledContext } from '../event-calendar/EventCalendarStyledContext';

const StandaloneEventRoot = styled(UnstyledStandaloneEvent, {
  name: 'MuiEventCalendar',
  slot: 'StandaloneEvent',
})({});

const StandaloneEvent = React.forwardRef<HTMLDivElement, StandaloneEventProps>(
  function StandaloneEvent(props, forwardedRef) {
    const styledContext = React.useContext(EventCalendarStyledContext);
    return (
      <StandaloneEventRoot
        ref={forwardedRef}
        className={styledContext?.classes.standaloneEvent}
        {...props}
        renderDragPreview={(parameters) => <EventDragPreview {...parameters} />}
      />
    );
  },
);

StandaloneEvent.propTypes /* remove-proptypes */ = {
  // ----------------------------- Warning --------------------------------
  // | These PropTypes are generated from the TypeScript type definitions |
  // | To update them edit the TypeScript types and run "pnpm proptypes"  |
  // ----------------------------------------------------------------------
  data: PropTypes.shape({
    allDay: PropTypes.bool,
    className: PropTypes.string,
    color: PropTypes.oneOf([
      'amber',
      'blue',
      'green',
      'grey',
      'indigo',
      'lime',
      'orange',
      'pink',
      'purple',
      'red',
      'teal',
    ]),
    description: PropTypes.string,
    draggable: PropTypes.bool,
    duration: PropTypes.number,
    exDates: PropTypes.arrayOf(PropTypes.string),
    extractedFromId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    readOnly: PropTypes.bool,
    resizable: PropTypes.oneOfType([PropTypes.oneOf(['end', 'start']), PropTypes.bool]),
    resource: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.string]),
    rrule: PropTypes.oneOfType([
      PropTypes.shape({
        byDay: PropTypes.arrayOf(
          PropTypes.oneOf([
            '-1FR',
            '-1MO',
            '-1SA',
            '-1SU',
            '-1TH',
            '-1TU',
            '-1WE',
            '-2FR',
            '-2MO',
            '-2SA',
            '-2SU',
            '-2TH',
            '-2TU',
            '-2WE',
            '-3FR',
            '-3MO',
            '-3SA',
            '-3SU',
            '-3TH',
            '-3TU',
            '-3WE',
            '-4FR',
            '-4MO',
            '-4SA',
            '-4SU',
            '-4TH',
            '-4TU',
            '-4WE',
            '-5FR',
            '-5MO',
            '-5SA',
            '-5SU',
            '-5TH',
            '-5TU',
            '-5WE',
            '1FR',
            '1MO',
            '1SA',
            '1SU',
            '1TH',
            '1TU',
            '1WE',
            '2FR',
            '2MO',
            '2SA',
            '2SU',
            '2TH',
            '2TU',
            '2WE',
            '3FR',
            '3MO',
            '3SA',
            '3SU',
            '3TH',
            '3TU',
            '3WE',
            '4FR',
            '4MO',
            '4SA',
            '4SU',
            '4TH',
            '4TU',
            '4WE',
            '5FR',
            '5MO',
            '5SA',
            '5SU',
            '5TH',
            '5TU',
            '5WE',
            'FR',
            'MO',
            'SA',
            'SU',
            'TH',
            'TU',
            'WE',
          ]).isRequired,
        ),
        byMonth: PropTypes.arrayOf(PropTypes.number),
        byMonthDay: PropTypes.arrayOf(PropTypes.number),
        count: PropTypes.number,
        freq: PropTypes.oneOf(['DAILY', 'MONTHLY', 'WEEKLY', 'YEARLY']).isRequired,
        interval: PropTypes.number,
        until: PropTypes.string,
      }),
      PropTypes.string,
    ]),
    timezone: PropTypes.string,
    title: PropTypes.string.isRequired,
  }).isRequired,
  /**
   * Callback fired when the event is dropped into the Event Calendar.
   */
  onEventDrop: PropTypes.func,
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.bool])),
    PropTypes.func,
    PropTypes.object,
  ]),
} as any;

export { StandaloneEvent };
